import React, { useState, useEffect, useRef } from 'react';
import { SubtitleCue, fetchAndParseSRT, getActiveSubtitle } from '../../utils/srtParser';
import './BilingualSubtitles.css';

interface BilingualSubtitlesProps {
  audioRef: React.RefObject<HTMLAudioElement>;
  subtitleUrlEn?: string;
  subtitleUrlNative?: string;
  displayMode: 'off' | 'en' | 'zh-tw' | 'both'; // NEW: 'both' for bilingual display
}

export const BilingualSubtitles: React.FC<BilingualSubtitlesProps> = ({
  audioRef,
  subtitleUrlEn,
  subtitleUrlNative,
  displayMode,
}) => {
  const [englishCues, setEnglishCues] = useState<SubtitleCue[]>([]);
  const [chineseCues, setChineseCues] = useState<SubtitleCue[]>([]);
  const [currentEnglish, setCurrentEnglish] = useState<string>('');
  const [currentChinese, setCurrentChinese] = useState<string>('');
  const animationFrameRef = useRef<number>();

  // Load subtitle files
  useEffect(() => {
    const loadSubtitles = async () => {
      if (subtitleUrlEn) {
        const enCues = await fetchAndParseSRT(subtitleUrlEn);
        setEnglishCues(enCues);
      }
      if (subtitleUrlNative) {
        const zhCues = await fetchAndParseSRT(subtitleUrlNative);
        setChineseCues(zhCues);
      }
    };

    loadSubtitles();
  }, [subtitleUrlEn, subtitleUrlNative]);

  // Update subtitles based on audio current time
  // Update frequency: 20fps (every 50ms) instead of 60fps for better performance
  useEffect(() => {
    if (!audioRef.current || displayMode === 'off') {
      setCurrentEnglish('');
      setCurrentChinese('');
      return;
    }

    const audio = audioRef.current;
    let lastUpdateTime = 0;
    const UPDATE_INTERVAL = 50; // Update every 50ms (20fps)

    const updateSubtitles = (timestamp: number) => {
      // Throttle updates to 20fps (every 50ms)
      if (timestamp - lastUpdateTime < UPDATE_INTERVAL) {
        animationFrameRef.current = requestAnimationFrame(updateSubtitles);
        return;
      }
      lastUpdateTime = timestamp;

      const currentTime = audio.currentTime;

      // Update English subtitle
      if ((displayMode === 'en' || displayMode === 'both') && englishCues.length > 0) {
        const activeCue = getActiveSubtitle(englishCues, currentTime);
        setCurrentEnglish(activeCue?.text || '');
      } else {
        setCurrentEnglish('');
      }

      // Update Chinese subtitle
      if ((displayMode === 'zh-tw' || displayMode === 'both') && chineseCues.length > 0) {
        const activeCue = getActiveSubtitle(chineseCues, currentTime);
        setCurrentChinese(activeCue?.text || '');
      } else {
        setCurrentChinese('');
      }

      // Continue updating using requestAnimationFrame
      animationFrameRef.current = requestAnimationFrame(updateSubtitles);
    };

    // Start updating
    animationFrameRef.current = requestAnimationFrame(updateSubtitles);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioRef, displayMode, englishCues, chineseCues]);

  // Don't render if off
  if (displayMode === 'off') {
    return null;
  }

  return (
    <div className="bilingual-subtitles">
      {displayMode === 'both' ? (
        // Bilingual display (both languages)
        <>
          <div className="subtitle-line subtitle-english">
            {currentEnglish || '\u00A0'}
          </div>
          <div className="subtitle-line subtitle-chinese">
            {currentChinese || '\u00A0'}
          </div>
        </>
      ) : displayMode === 'en' ? (
        // English only
        <div className="subtitle-line subtitle-english-only">
          {currentEnglish || '\u00A0'}
        </div>
      ) : displayMode === 'zh-tw' ? (
        // Chinese only
        <div className="subtitle-line subtitle-chinese-only">
          {currentChinese || '\u00A0'}
        </div>
      ) : null}
    </div>
  );
};
